import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cmdbBaselinesQuery, cmdbRemoveBaseline, cmdbTakeBaseline, type Baseline } from '../api/cmdb'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { LoadFailed } from '../ui/LoadFailed'
import { toast } from '../ui/notify'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CmdbViews } from './CmdbViews'

/**
 * `Cmdb::baselines` - starea la un moment dat, la /app/cmdb/baselines.
 *
 * Adresa e cea din portal: fila "State" din strip-ul celor trei ecrane CMDB
 * (`inventoree/_views.php`) trimite acolo.
 *
 * DE CE NU E ACELASI LUCRU CU CHANGES
 *
 * Se spune pe ecran, fiindca altfel intrebarea evidenta - "dar avem deja
 * Changes" - ramane fara raspuns si ecranul pare o dublura. Jurnalul spune ce
 * s-a intamplat, eveniment cu eveniment, si raspunde numai daca fiecare
 * schimbare a fost prinsa. O fotografie nu poate minti asa: ce era in ea, era;
 * ce nu e acum, nu e.
 */

const STYLES = ['css/adm.css']

export function CmdbBaselinesPage() {
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(cmdbBaselinesQuery)
  const query = useQueryClient()

  const [name, setName] = useState('')
  const [scope, setScope] = useState(0)
  const [busy, setBusy] = useState(false)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  /*
   * O CERERE PICATA SE SPUNE.
   *
   * Garda de dedesubt - `if (!page.data)` - se sprijina pe presupunerea ca
   * datele vin. Cand capatul raspunde 500, `data` ramane `undefined` pentru
   * totdeauna, si ecranul ramane pe `Spinner`: trei bile FARA text, adica
   * exact nimic pe ecran. Asa arata ecranul asta cand s-a plans omul de el, si
   * din afara nu se putea deosebi de o pagina rupta.
   *
   * Motivul vine de la capat (`ApiError.message`), deci "tabela nu exista" sau
   * "n-am putut citi" ajung la om, nu in consola browserului.
   */
  if (page.error) {
    return (
      <Content>
        <div style={{ padding: '12px 16px' }}>
          <LoadFailed error={page.error} onRetry={() => void page.refetch()} what="The snapshots" />
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  const reload = () => void query.invalidateQueries({ queryKey: ['cmdb', 'baselines'] })

  if (!page.data.hasTables) {
    return (
      <Content>
        {/* Banda ramane si cand tabelele lipsesc: ecranul nu e o fundatura. */}
        <CmdbViews views={page.data.views} current="cmdb/baselines" />
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <h4>
            <i className="fas fa-database"></i> State at a point in time
          </h4>
          <p>The snapshot tables are not in the database yet.</p>
          <p className="text-muted small" style={{ marginBottom: 0 }}>
            This screen works as soon as the migration runs:{' '}
            <code>2026_08_26_190008_create_it_ci_baseline</code>.
          </p>
        </div>
      </Content>
    )
  }

  const take = (event: React.FormEvent) => {
    event.preventDefault()
    if (name.trim() === '') return
    setBusy(true)
    void cmdbTakeBaseline({ name, scope_ci_id: scope })
      .then((result) => {
        setBusy(false)
        if (result.status !== 'OK') {
          toast(result.message, 'CMDB', 'error')
          return
        }
        toast(`${result.ci_count ?? 0} items and ${result.edge_count ?? 0} links photographed.`, 'CMDB')
        setName('')
        reload()
      })
      .catch(() => {
        setBusy(false)
        toast('That could not be saved.', 'CMDB', 'error')
      })
  }

  const drop = (id: number) => {
    if (!window.confirm('Remove this snapshot? What it recorded goes with it.')) return
    void cmdbRemoveBaseline(id)
      .then((result) => {
        if (result.status !== 'OK') {
          toast(result.message, 'CMDB', 'error')
          return
        }
        toast(result.message, 'CMDB')
        reload()
      })
      .catch(() => toast('That could not be saved.', 'CMDB', 'error'))
  }

  return (
    <Content>
      <div id="content">
        {/* Banda celor patru vederi, prima ca pe celelalte trei ecrane CMDB. */}
        <CmdbViews views={page.data.views} current="cmdb/baselines" />
        <div className="adm-panel" style={{ marginBottom: 16 }}>
          <p style={{ margin: 0 }}>
            A snapshot holds the state of the items and the links between them <strong>as they were</strong>, so you can
            ask later what is different.
          </p>
          <p className="text-muted small" style={{ margin: '6px 0 0' }}>
            It is not the same thing as <a href="/app/adm/changes">Changes</a>: that one tells you what happened, event
            by event, and only answers if every change was caught. A snapshot cannot lie that way &mdash; what was in
            it, was; what is not here now, is not.
          </p>
        </div>

        {page.data.canWrite && (
          <form id="new-baseline-form" className="form-inline" style={{ marginBottom: 18 }} onSubmit={take}>
            <div className="form-group" style={{ marginRight: 8 }}>
              <input
                type="text"
                className="form-control input-sm"
                required
                maxLength={190}
                placeholder="Snapshot name"
                style={{ width: 260 }}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="form-group" style={{ marginRight: 8 }}>
              {/* Numai serviciile: o fotografie se face inaintea unei schimbari
                  pe ceva anume, iar "ceva anume" e un serviciu, nu o statie. */}
              <select
                className="form-control input-sm"
                style={{ width: 260 }}
                value={scope}
                onChange={(e) => setScope(Number(e.target.value))}
              >
                <option value={0}>Everything we hold</option>
                {page.data.services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.name} and everything under it
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
              <i className="fas fa-camera"></i> Take a snapshot now
            </button>
          </form>
        )}

        {page.data.baselines.length === 0 ? (
          <div className="adm-panel">
            <p style={{ margin: 0 }}>No snapshot yet.</p>
            <p className="text-muted small" style={{ margin: '6px 0 0' }}>
              {/* Momentul in care merita facuta e inaintea unei schimbari, iar
                  cine citeste ecranul prima oara n-are de unde sti asta. */}
              The right moment is <strong>before</strong> a planned change: that is when the snapshot answers, a week
              later, the question &bdquo;what did we move?&rdquo;
            </p>
            {/* Lista goala e un raspuns, nu o pagina pe jumatate desenata - deci
                spune si CU CE se face prima fotografie, ca omul sa nu plece de
                pe ecran ca sa caute butonul. */}
            <p className="text-muted small" style={{ margin: '6px 0 0' }}>
              {page.data.canWrite
                ? 'The first one is made with "Take a snapshot now", above: choose what to photograph, give it a name, and it records how everything looks at this moment.'
                : 'Taking one needs the right to change the register - ask whoever holds it.'}
            </p>
          </div>
        ) : (
          <table className="table table-hover adm-table">
            <thead>
              <tr>
                <th>Snapshot</th>
                <th style={{ width: 220 }}>Scope</th>
                <th style={{ width: 150 }}>When</th>
                <th style={{ width: 90 }} className="center">
                  Items
                </th>
                <th style={{ width: 90 }} className="center">
                  Links
                </th>
                <th style={{ width: 180 }} className="center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {page.data.baselines.map((row) => (
                <tr key={row.id}>
                  <td>
                    <a href={`/app/cmdb/compareBaseline/${row.id}`}>{row.name}</a>
                    {row.note !== null && row.note !== '' && <div className="text-muted small">{row.note}</div>}
                  </td>
                  <td>
                    <Scope row={row} />
                  </td>
                  <td className="text-muted">{row.takenAt}</td>
                  <td className="center">{row.ciCount}</td>
                  <td className="center">{row.edgeCount}</td>
                  <td className="center">
                    <a className="btn btn-xs btn-primary" href={`/app/cmdb/compareBaseline/${row.id}`}>
                      <i className="fas fa-exchange-alt"></i> What is different now
                    </a>
                    {page.data.canWrite && (
                      <button className="btn btn-xs btn-default remove-baseline" onClick={() => drop(row.id)}>
                        <i className="fas fa-trash"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Content>
  )
}

function Scope({ row }: { row: Baseline }) {
  if (row.scopeCiId === 0) return <span className="text-muted">everything we hold</span>
  if (row.scopeName !== null && row.scopeName !== '') return <>{row.scopeName}</>
  /* Elementul pe care s-a facut a fost sters intre timp - chiar asta e o
     informatie, nu o coloana goala. */
  return <span className="adm-pill adm-pill-bad">deleted item #{row.scopeCiId}</span>
}
